/**
 * LiveBracketBlock — Renders a live tournament bracket within a hub content block.
 *
 * Fetches tournament data by competition ID and renders the existing BracketViewer
 * component. Includes loading/error states and a link to the full tournament page.
 *
 * The bracket auto-refreshes via React Query's staleTime (2 min default from
 * useGetTournament) — no WebSocket needed for the content block view.
 */

import {ReactElement} from "react";
import {useNavigate} from "react-router";
import {useGetTournament} from "@/competitions/tournaments/hooks/useGetTournament";
import {BracketViewer} from "@/competitions/tournaments/Bracket";
import "./LiveBracketBlock.scss";

interface LiveBracketBlockProps {
    bracketCompetitionId?: string;
    sectionTitle?: string;
}

export function LiveBracketBlock({bracketCompetitionId, sectionTitle}: LiveBracketBlockProps): ReactElement {
    const navigate = useNavigate();
    const {data, isLoading, isError} = useGetTournament(bracketCompetitionId);

    if (!bracketCompetitionId) return <></>;

    if (isLoading) {
        return (
            <div className="live-bracket-block">
                {sectionTitle && <h3 className="live-bracket-block__title">{sectionTitle}</h3>}
                <div className="live-bracket-block__loading">
                    <div className="live-bracket-block__spinner" />
                    <p>Loading bracket...</p>
                </div>
            </div>
        );
    }

    if (isError || !data?.tournament?.bracket) {
        return (
            <div className="live-bracket-block">
                {sectionTitle && <h3 className="live-bracket-block__title">{sectionTitle}</h3>}
                <div className="live-bracket-block__empty">
                    <p>Bracket not available yet</p>
                </div>
            </div>
        );
    }

    const {tournament, teams} = data;

    return (
        <div className="live-bracket-block">
            <div className="live-bracket-block__header">
                <h3 className="live-bracket-block__title">
                    {sectionTitle || tournament.name}
                </h3>
                <button
                    className="live-bracket-block__view-full"
                    onClick={() => navigate(`/tournaments/${bracketCompetitionId}`)}
                >
                    View Full Tournament
                </button>
            </div>

            <div className="live-bracket-block__bracket-wrapper">
                <BracketViewer
                    tournamentId={tournament.id}
                    bracket={tournament.bracket}
                    teams={teams}
                    compact={true}
                    showSeeds={true}
                />
            </div>
        </div>
    );
}
